import { useEffect } from "react";
import { Helmet } from "react-helmet";
import { Navigate } from "react-router-dom";
import { useQuery } from "@apollo/client";
import { formatDistanceToNow } from "date-fns";
import Box from "@mui/material/Box";
import Grid from "@mui/material/Grid";
import Paper from "@mui/material/Paper";
import Stack from "@mui/material/Stack";
import Table from "@mui/material/Table";
import TableBody from "@mui/material/TableBody";
import TableContainer from "@mui/material/TableContainer";
import TableHead from "@mui/material/TableHead";
import Typography from "@mui/material/Typography";

import { useAppDispatch, useAppSelector } from "../../../app/hooks";
import { ACTIVE_SESSIONS } from "../../../apollo/queries/users";
import { StyledTableCell, StyledTableRow } from "../../../styles/tableStyles";
import { selectAuth } from "../../auth/authSlice";
import { UserTypes } from "../../auth/types/authType";
import { changePageTitle } from "../../preferences/preferencesSlice";

type ActiveSessionRow = {
  _id: string;
  name: string;
  email: string;
  companyName: string;
  lastActiveAt: string;
};

const lastActiveLabel = (value: string) => {
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return "";
  return formatDistanceToNow(date, { addSuffix: true });
};

export const ActiveSessions = () => {
  const dispatch = useAppDispatch();
  const { user } = useAppSelector(selectAuth);
  const isSuper = user?.userType === UserTypes.Super;

  const { data, loading } = useQuery(ACTIVE_SESSIONS, {
    skip: !isSuper,
    pollInterval: 30000,
    fetchPolicy: "network-only",
  });

  useEffect(() => {
    dispatch(changePageTitle("Active Sessions"));
  }, [dispatch]);

  if (!isSuper) return <Navigate to="/app/profile" />;

  const sessions = (data?.activeSessions ?? []) as ActiveSessionRow[];

  return (
    <>
      <Helmet>
        <title>Active Sessions | EnjazPlus</title>
      </Helmet>
      <Box
        sx={{
          m: { xs: 0, md: 1 },
          px: { xs: 0, md: 2 },
        }}
      >
        <Grid container justifyContent="flex-start" sx={{ mt: 1 }}>
          <TableContainer component={Paper}>
            <Table size="small" aria-label="active sessions">
              <TableHead>
                <StyledTableRow>
                  <StyledTableCell>S.No</StyledTableCell>
                  <StyledTableCell>Name</StyledTableCell>
                  <StyledTableCell>Email</StyledTableCell>
                  <StyledTableCell>Company</StyledTableCell>
                  <StyledTableCell>Last active</StyledTableCell>
                </StyledTableRow>
              </TableHead>
              <TableBody>
                {loading && sessions.length === 0 ? (
                  <StyledTableRow>
                    <StyledTableCell colSpan={5}>Loading...</StyledTableCell>
                  </StyledTableRow>
                ) : sessions.length === 0 ? (
                  <StyledTableRow>
                    <StyledTableCell colSpan={5}>
                      No one is active right now
                    </StyledTableCell>
                  </StyledTableRow>
                ) : (
                  sessions.map((row, index) => (
                    <StyledTableRow key={row._id}>
                      <StyledTableCell component="th" scope="row">
                        {index + 1}
                      </StyledTableCell>
                      <StyledTableCell>{row.name}</StyledTableCell>
                      <StyledTableCell>{row.email}</StyledTableCell>
                      <StyledTableCell>{row.companyName}</StyledTableCell>
                      <StyledTableCell>
                        {lastActiveLabel(row.lastActiveAt)}
                      </StyledTableCell>
                    </StyledTableRow>
                  ))
                )}
              </TableBody>
            </Table>
          </TableContainer>
        </Grid>
        <Stack spacing={1} sx={{ mt: 1 }}>
          <Typography variant="h6" color="textPrimary" component="div">
            Active users: {sessions.length}
          </Typography>
        </Stack>
      </Box>
    </>
  );
};
